/** File-type classification: labels, colours, MIME mapping and preview support. */

const TYPES = {
  pdf: { label: 'PDF', color: 'var(--ft-pdf)', name: 'PDF document', mime: 'application/pdf', preview: true },
  doc: { label: 'DOC', color: 'var(--ft-word)', name: 'Word document', mime: 'application/msword', preview: true },
  docx: {
    label: 'DOCX',
    color: 'var(--ft-word)',
    name: 'Word document',
    mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    preview: true,
  },
  xls: { label: 'XLS', color: 'var(--ft-excel)', name: 'Excel workbook', mime: 'application/vnd.ms-excel', preview: true },
  xlsx: {
    label: 'XLSX',
    color: 'var(--ft-excel)',
    name: 'Excel workbook',
    mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    preview: true,
  },
  csv: { label: 'CSV', color: 'var(--ft-excel)', name: 'CSV file', mime: 'text/csv', preview: true },
  ppt: { label: 'PPT', color: 'var(--ft-ppt)', name: 'Presentation', mime: 'application/vnd.ms-powerpoint', preview: true },
  pptx: {
    label: 'PPTX',
    color: 'var(--ft-ppt)',
    name: 'Presentation',
    mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    preview: true,
  },
  png: { label: 'PNG', color: 'var(--ft-image)', name: 'Image', mime: 'image/png', preview: true },
  jpg: { label: 'JPG', color: 'var(--ft-image)', name: 'Image', mime: 'image/jpeg', preview: true },
  jpeg: { label: 'JPEG', color: 'var(--ft-image)', name: 'Image', mime: 'image/jpeg', preview: true },
  svg: { label: 'SVG', color: 'var(--ft-image)', name: 'Vector image', mime: 'image/svg+xml', preview: true },
  zip: { label: 'ZIP', color: 'var(--ft-archive)', name: 'Archive', mime: 'application/zip', preview: false },
  rar: { label: 'RAR', color: 'var(--ft-archive)', name: 'Archive', mime: 'application/vnd.rar', preview: false },
  txt: { label: 'TXT', color: 'var(--ft-text)', name: 'Text file', mime: 'text/plain', preview: true },
  md: { label: 'MD', color: 'var(--ft-text)', name: 'Markdown', mime: 'text/markdown', preview: true },
  mp4: { label: 'MP4', color: 'var(--ft-video)', name: 'Video', mime: 'video/mp4', preview: false },
  dwg: { label: 'DWG', color: 'var(--ft-text)', name: 'CAD drawing', mime: 'image/vnd.dwg', preview: false },
};

const FALLBACK = { label: 'FILE', color: 'var(--ft-text)', name: 'File', mime: 'application/octet-stream', preview: false };

export function getExtension(fileName = '') {
  const i = String(fileName).lastIndexOf('.');
  return i > 0 ? fileName.slice(i + 1).toLowerCase() : '';
}

export function getFileType(fileNameOrExt = '') {
  const ext = fileNameOrExt.includes('.') ? getExtension(fileNameOrExt) : String(fileNameOrExt).toLowerCase();
  return { ext, ...(TYPES[ext] || FALLBACK) };
}

export function isImage(fileName) {
  return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(getExtension(fileName));
}

export function canPreview(fileName) {
  return getFileType(fileName).preview;
}

/** Coarse groups used by search facets and the file-type filter. */
export const FILE_TYPE_GROUPS = [
  { key: 'pdf', label: 'PDF', exts: ['pdf'] },
  { key: 'document', label: 'Word', exts: ['doc', 'docx', 'txt', 'md'] },
  { key: 'spreadsheet', label: 'Excel', exts: ['xls', 'xlsx', 'csv'] },
  { key: 'presentation', label: 'PowerPoint', exts: ['ppt', 'pptx'] },
  { key: 'image', label: 'Image', exts: ['png', 'jpg', 'jpeg', 'svg'] },
  { key: 'archive', label: 'Archive', exts: ['zip', 'rar'] },
  { key: 'other', label: 'Other', exts: ['mp4', 'dwg'] },
];

export function getTypeGroup(fileName) {
  const ext = getExtension(fileName);
  return FILE_TYPE_GROUPS.find((g) => g.exts.includes(ext))?.key || 'other';
}

/** Upload allowlist mirrored from system settings; the server re-validates. */
export const ACCEPTED_EXTENSIONS = Object.keys(TYPES);

export const ACCEPT_ATTRIBUTE = ACCEPTED_EXTENSIONS.map((e) => `.${e}`).join(',');
